/**
 * Item Value Entry Component
 * 
 * Provides UI for entering attribute values for items within a session.
 * Respects session mode (live/async) and entry mode (facilitator/participants).
 * 
 * Features:
 * - Shows candidates missing values for session criteria
 * - Allows value entry based on user permissions
 * - Integrates with value proposal workflow
 * - Supports batch entry for multiple candidates
 */

import { useState, useMemo, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  HelpCircle,
  RotateCcw,
} from 'lucide-react';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { Attribute } from '../../../types/prioritization/lifecycle';
import { valueScopeKind, VALUE_SCOPE_LABEL } from '../../../lib/valueScope';
import { saveSessionValue } from '../../../lib/sessionValueEntry';

// ============================================================================
// Types
// ============================================================================

export interface ItemValueEntryProps {
  session: PrioritizationSession;
  itemId: string;
  onValuesUpdated?: () => void;
}

export interface BatchValueEntryProps {
  session: PrioritizationSession;
  onValuesUpdated?: () => void;
}

interface ValueEntryState {
  [attributeKey: string]: unknown;
}

// ============================================================================
// Single Item Value Entry
// ============================================================================

export function ItemValueEntry({ 
  session, 
  itemId,
  onValuesUpdated,
}: ItemValueEntryProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValues, setLocalValues] = useState<ValueEntryState>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  const item = useItemsStore(state =>
    state.items.find(c => c.id === itemId)
  );
  const attributes = useAttributesStore(state => state.attributes);
  const { acceptException, removeException } = useValueProposalStore();
  const exceptions = useValueProposalStore(state => state.exceptions);
  const proposals = useValueProposalStore(state => state.proposals);
  const acceptedValues = useValueProposalStore(state => state.acceptedValues);
  const currentUserId = useUserStore((s) => s.userId);
  const currentUserName = useUserStore((s) => s.userName);
  const currentRole = useUserStore((s) => s.currentRole);
  const can = useUserStore((s) => s.can);
  // Entering a value in a session is a contribution (values.propose). Making it
  // this session's value is an acceptance (values.accept). A Values Collector
  // holds only the first, so the two are asked separately.
  const mayContribute = can('values.propose');
  const mayAcceptForSession = can('values.accept');

  const eligibility = session.itemEligibility[itemId];
  const sessionCriteria = session.criteriaConfig.criteriaIds;

  // Attributes flagged as unknown for this item (session-scoped or global).
  // "Flag as unknown" is a first-class answer, not an escape hatch (brief-X §2.4;
  // Doc 26 rule 3) — backed by accepted_exceptions ("unknown value, proceed anyway").
  const flaggedUnknown = useMemo(
    () => exceptions.filter(e =>
      e.itemId === itemId && (!e.sessionId || e.sessionId === session.id)
    ),
    [exceptions, itemId, session.id]
  );
  const flaggedKeys = useMemo(
    () => new Set(flaggedUnknown.map(e => e.attributeKey)),
    [flaggedUnknown]
  );

  // Get attributes that need values (excluding those flagged as unknown)
  const attributesNeedingValues = useMemo(() => {
    return attributes.filter(attr =>
      sessionCriteria.includes(attr.key) &&
      eligibility?.missingAcceptedValues?.includes(attr.key) &&
      !flaggedKeys.has(attr.key)
    );
  }, [attributes, sessionCriteria, eligibility, flaggedKeys]);

  const [flaggingKey, setFlaggingKey] = useState<string | null>(null);
  const handleFlagUnknown = async (attributeKey: string) => {
    if (!currentUserId || flaggingKey) return;
    setFlaggingKey(attributeKey);
    try {
      await acceptException({
        itemId,
        attributeKey,
        reason: 'Flagged as unknown during value entry',
        scope: 'session',
        sessionId: session.id,
        acceptedBy: currentUserId,
      });
      onValuesUpdated?.();
    } finally {
      setFlaggingKey(null);
    }
  };
  const handleUnflag = async (attributeKey: string) => {
    await removeException(itemId, attributeKey, session.id);
    onValuesUpdated?.();
  };
  
  // Check if user can enter values
  const canEnterValues = useMemo(() => {
    // In facilitator mode, only the facilitator enters values
    if (session.modeConfig?.entryMode === 'facilitator') {
      return session.facilitatorId === currentUserId && (mayContribute || mayAcceptForSession);
    }
    return mayContribute || mayAcceptForSession;
  }, [session, currentUserId, mayContribute, mayAcceptForSession]);

  /**
   * What is already on record for one attribute, in words a person reads:
   * the official value, this session's value, and the person's own
   * contribution waiting to be accepted. Another session's value is not shown
   * here — it does not count in this session.
   */
  const describeRecorded = (attributeKey: string): string[] => {
    const lines: string[] = [];
    for (const av of acceptedValues) {
      if (av.itemId !== itemId || av.attributeKey !== attributeKey) continue;
      const kind = valueScopeKind(av, session.id);
      if (kind === 'other_session') continue;
      lines.push(`${VALUE_SCOPE_LABEL[kind]}: ${String(av.value ?? '')}`);
    }
    const mine = proposals.find(p =>
      p.itemId === itemId &&
      p.attributeKey === attributeKey &&
      p.sessionId === session.id &&
      p.proposedBy === currentUserId &&
      p.status === 'proposed'
    );
    if (mine) {
      lines.push(`Your contribution: ${String(mine.proposedValue ?? '')} — waiting to be accepted as official`);
    }
    return lines;
  };
  
  if (!item) {
    return null;
  }
  
  const handleStartEdit = () => {
    // Initialize local values from current values
    const initial: ValueEntryState = {};
    for (const attr of attributesNeedingValues) {
      const existing = item.attributeValues?.[attr.key];
      initial[attr.key] = existing ?? '';
    }
    setLocalValues(initial);
    setIsEditing(true);
  };
  
  /**
   * Save what was typed (roadmap 1C, V-1 / V-6) through the one session-entry
   * path (src/lib/sessionValueEntry.ts): a contribution for everyone who may
   * contribute, plus this session's value when the person may accept values.
   * Before this, the save called the accept action for everyone without
   * waiting for it, so a person without that permission saw the form close
   * and nothing saved. Every refusal is now shown.
   */
  const handleSaveValues = async () => {
    if (!currentUserId) return;
    
    setIsSaving(true);
    setSaveError(null);
    const failures: string[] = [];
    try {
      for (const [attrKey, value] of Object.entries(localValues)) {
        if (value === undefined || value === '') continue;
        const label = attributes.find(a => a.key === attrKey)?.label ?? attrKey;
        const result = await saveSessionValue({
          itemId,
          attributeKey: attrKey,
          value,
          sessionId: session.id,
          sessionName: session.name,
          trackId: session.trackId,
          userId: currentUserId,
          userName: currentUserName,
          role: currentRole,
          mayAcceptForSession,
          acceptanceMethod: session.modeConfig?.entryMode === 'facilitator' ? 'facilitator_override' : 'consensus',
        });
        if (result.error) failures.push(`${label}: ${result.error}`);
      }
      
      if (failures.length > 0) {
        setSaveError(failures.join(' · '));
      } else {
        setIsEditing(false);
      }
      onValuesUpdated?.();
    } finally {
      setIsSaving(false);
    }
  };
  
  const renderValueInput = (attribute: Attribute) => {
    const value = localValues[attribute.key] ?? '';
    
    switch (attribute.type) {
      case 'select':
        return (
          <select
            value={String(value)}
            onChange={(e) => setLocalValues(prev => ({ ...prev, [attribute.key]: e.target.value }))}
            className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select...</option>
            {attribute.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            onChange={(e) => setLocalValues(prev => ({ 
              ...prev, 
              [attribute.key]: e.target.value ? Number(e.target.value) : '' 
            }))}
            min={attribute.validation?.min}
            max={attribute.validation?.max}
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Enter ${attribute.label}`}
          />
        );
      
      case 'boolean':
        return (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`${itemId}-${attribute.key}`}
                checked={value === true}
                onChange={() => setLocalValues(prev => ({ ...prev, [attribute.key]: true }))}
                className="text-primary-600"
              />
              <span className="text-sm">Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`${itemId}-${attribute.key}`}
                checked={value === false}
                onChange={() => setLocalValues(prev => ({ ...prev, [attribute.key]: false }))}
                className="text-primary-600"
              />
              <span className="text-sm">No</span>
            </label>
          </div>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={String(value)}
            onChange={(e) => setLocalValues(prev => ({ ...prev, [attribute.key]: e.target.value }))}
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => setLocalValues(prev => ({ ...prev, [attribute.key]: e.target.value }))}
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Enter ${attribute.label}`}
          />
        );
    }
  };
  
  // Flagged-as-unknown list — shown alongside both the summary and edit states
  const flaggedList = flaggedUnknown.length > 0 && (
    <div className="px-4 py-3 space-y-1.5">
      {flaggedUnknown.map(exc => (
        <div key={exc.attributeKey} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-[var(--text-ter)]">
            <HelpCircle className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            {attributes.find(a => a.key === exc.attributeKey)?.label ?? exc.attributeKey}
            <span className="text-xs text-[var(--text-ter)]">— unknown, proceeding without it</span>
          </span>
          {canEnterValues && (
            <button
              onClick={() => void handleUnflag(exc.attributeKey)}
              className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              <RotateCcw className="h-3 w-3" />
              Undo
            </button>
          )}
        </div>
      ))}
    </div>
  );

  // If all values are present (or flagged unknown), show summary
  if (attributesNeedingValues.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] px-4 py-3">
          <CheckCircle className="h-4 w-4" />
          <span>
            {flaggedUnknown.length > 0
              ? 'All values entered or flagged as unknown'
              : 'All values entered'}
          </span>
        </div>
        {flaggedList}
      </div>
    );
  }
  
  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)] flex items-center justify-between">
        <div>
          <h4 className="font-medium text-[var(--text-pri)]">{item.title}</h4>
          <p className="text-sm text-[var(--mustard)] flex items-center gap-1 mt-0.5">
            <AlertCircle className="h-3.5 w-3.5" />
            {attributesNeedingValues.length} value{attributesNeedingValues.length !== 1 ? 's' : ''} needed
          </p>
        </div>
        
        {canEnterValues && !isEditing && (
          <button
            onClick={handleStartEdit}
            className="btn btn-secondary text-sm flex items-center gap-1"
          >
            <Edit2 className="h-4 w-4" />
            Enter Values
          </button>
        )}
      </div>
      
      {/* Edit Form */}
      {isEditing && (
        <div className="p-4 space-y-4">
          {attributesNeedingValues.map(attr => (
            <div key={attr.key}>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-0.5">
                {attr.label}
                {attr.required && <span className="text-[var(--coral)] ml-1">*</span>}
              </label>
              {attr.definition && (
                <p className="text-xs text-[var(--text-ter)] mb-1.5 leading-relaxed">{attr.definition}</p>
              )}
              {renderValueInput(attr)}
              {describeRecorded(attr.key).map(line => (
                <p key={line} className="mt-1 text-xs text-[var(--text-ter)]">{line}</p>
              ))}
              <button
                type="button"
                onClick={() => void handleFlagUnknown(attr.key)}
                disabled={flaggingKey === attr.key}
                className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-50"
              >
                <HelpCircle className="h-3 w-3" />
                {flaggingKey === attr.key ? 'Flagging…' : "I don't know this — flag as unknown"}
              </button>
            </div>
          ))}
          
          <p className="text-xs text-[var(--text-ter)]">
            {mayAcceptForSession
              ? 'What you save here becomes this session\'s value only. It is also offered up, so someone who approves values can make it the official value for the workspace.'
              : 'What you save here is your contribution. It is offered up, so someone who approves values can accept it — for this session or as the official value for the workspace.'}
          </p>
          {saveError && (
            <p role="alert" className="text-sm text-[var(--coral)]">{saveError}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={() => setIsEditing(false)}
              className="btn btn-secondary text-sm flex items-center gap-1"
              disabled={isSaving}
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              onClick={handleSaveValues}
              className="btn btn-primary text-sm flex items-center gap-1"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Values'}
            </button>
          </div>
        </div>
      )}
      
      {/* Read-only view when not editing */}
      {!isEditing && (
        <div className="p-4">
          <div className="grid gap-2">
            {attributesNeedingValues.map(attr => (
              <div key={attr.key} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[var(--text-sec)]">{attr.label}</span>
                  <span className="flex items-center gap-3">
                    {describeRecorded(attr.key).length > 0 ? (
                      <span className="text-[var(--text-sec)] text-xs text-right">
                        {describeRecorded(attr.key).join(' · ')}
                      </span>
                    ) : (
                      <span className="text-[var(--text-ter)] italic">Not set</span>
                    )}
                    {canEnterValues && (
                      <button
                        type="button"
                        onClick={() => void handleFlagUnknown(attr.key)}
                        disabled={flaggingKey === attr.key}
                        className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-50"
                      >
                        <HelpCircle className="h-3 w-3" />
                        Flag as unknown
                      </button>
                    )}
                  </span>
                </div>
                {attr.definition && (
                  <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-relaxed">{attr.definition}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attributes already flagged as unknown */}
      {flaggedList && <div className="border-t border-[var(--rule)]">{flaggedList}</div>}
    </div>
  );
}

// ============================================================================
// Batch Value Entry (for multiple candidates)
// ============================================================================

export function BatchValueEntry({
  session,
  onValuesUpdated,
}: BatchValueEntryProps) {
  const [expandedCandidates, setExpandedCandidates] = useState<Set<string>>(new Set());

  const items = useItemsStore(state => state.items);
  const fetchValueState = useValueProposalStore(state => state.fetchFromSupabase);
  const exceptions = useValueProposalStore(state => state.exceptions);

  // Hydrate proposals/accepted-values/exceptions so previously flagged
  // "unknown" attributes render after a direct link or refresh (§2.4).
  useEffect(() => {
    void fetchValueState();
  }, [fetchValueState]);

  // Get items with gaps (need values)
  const candidatesWithGaps = useMemo(() => {
    return session.itemIds
      .map(id => ({
        id,
        candidate: items.find(c => c.id === id),
        eligibility: session.itemEligibility[id],
      }))
      .filter(({ eligibility }) =>
        eligibility?.status === 'ELIGIBLE_WITH_GAPS'
      );
  }, [session.itemIds, session.itemEligibility, items]);

  // Progress (brief-X §2.4): an item counts as resolved when every missing
  // value is either entered or flagged as unknown for this session.
  const resolvedCount = useMemo(() => {
    const exceptionKeysByItem = new Map<string, Set<string>>();
    for (const exc of exceptions) {
      if (exc.sessionId && exc.sessionId !== session.id) continue;
      const set = exceptionKeysByItem.get(exc.itemId) ?? new Set<string>();
      set.add(exc.attributeKey);
      exceptionKeysByItem.set(exc.itemId, set);
    }
    return candidatesWithGaps.filter(({ id, eligibility }) => {
      const missing = eligibility?.missingAcceptedValues ?? [];
      const flagged = exceptionKeysByItem.get(id);
      return missing.every(key => flagged?.has(key));
    }).length;
  }, [candidatesWithGaps, exceptions, session.id]);

  const totalItems = session.itemIds.length;
  const completeItems = totalItems - candidatesWithGaps.length + resolvedCount;
  
  const toggleExpanded = (id: string) => {
    setExpandedCandidates(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  /**
   * WHAT THIS COMPONENT CAN AND CANNOT SEE (defect, 2026-09-03).
   *
   * Gaps here come from `session.itemEligibility`, which tracks missing
   * ACCEPTED ATTRIBUTE VALUES in the item registry, keyed by attribute key. A
   * session's own criteria may instead be session-authored (`crit_*` ids that
   * exist only inside this event). Those ids can never appear in
   * `missingAcceptedValues`, so this component finds nothing and — until this
   * change — told the participant "All candidates have values, ready to
   * proceed" on the one step whose entire purpose was for them to enter those
   * values. It was not reporting a finished job; it was reporting a job it had
   * no way to look at.
   *
   * So: empty means "nothing to show" only when the criteria are attribute
   * keys this model can actually address. When they are not, say so.
   */
  const sessionCriteriaIds = session.criteriaConfig?.criteriaIds ?? [];
  const addressableCriteria = sessionCriteriaIds.filter((id) => !id.startsWith('crit_'));
  const cannotAddress =
    sessionCriteriaIds.length > 0 && addressableCriteria.length === 0;

  if (candidatesWithGaps.length === 0) {
    if (cannotAddress) {
      return (
        <div className="text-center py-8 text-[var(--text-ter)]">
          <AlertCircle className="h-12 w-12 mx-auto text-[var(--mustard)] mb-3" />
          <p className="font-medium text-[var(--text-pri)]">
            This step&apos;s criteria cannot be entered here
          </p>
          <p className="text-sm mt-1 max-w-md mx-auto">
            {sessionCriteriaIds.length} criteri{sessionCriteriaIds.length === 1 ? 'on was' : 'a were'}{' '}
            authored inside this session, and this surface only collects values for workspace
            attributes. Nothing has been recorded for you — ask the facilitator to open the
            scoring tool for this step.
          </p>
        </div>
      );
    }
    return (
      <div className="text-center py-8 text-[var(--text-ter)]">
        <CheckCircle className="h-12 w-12 mx-auto text-[var(--green)] mb-3" />
        <p className="font-medium">All candidates have values</p>
        <p className="text-sm mt-1">Ready to proceed to prioritization</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-[var(--text-pri)]">Candidates Needing Values</h3>
          <p className="text-sm text-[var(--text-sec)] mt-0.5">
            {candidatesWithGaps.length} candidate{candidatesWithGaps.length !== 1 ? 's' : ''} missing values for session criteria
          </p>
        </div>
      </div>

      {/* Progress — brief-X §2.4: "Progress: N of N items complete" */}
      {totalItems > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs text-[var(--text-ter)] mb-1">
            <span>Progress</span>
            <span>{completeItems} of {totalItems} item{totalItems !== 1 ? 's' : ''} complete</span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--hover-tint)] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${completeItems >= totalItems ? 'bg-[var(--green)]' : 'bg-primary-500'}`}
              style={{ width: `${Math.round((completeItems / totalItems) * 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className="space-y-3">
        {candidatesWithGaps.map(({ id, candidate, eligibility }) => (
          <div key={id} className="border border-[var(--mustard)] overflow-hidden">
            <button
              onClick={() => toggleExpanded(id)}
              className="w-full px-4 py-3 bg-[var(--mustard-dim)] flex items-center justify-between hover:bg-[var(--mustard-dim)] transition-colors"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-[var(--mustard)]" />
                <div className="text-left">
                  <p className="font-medium text-[var(--text-pri)]">{candidate?.title || 'Unknown'}</p>
                  <p className="text-sm text-[var(--mustard)]">
                    {eligibility?.missingAcceptedValues?.length || 0} missing values
                  </p>
                </div>
              </div>
              {expandedCandidates.has(id) ? (
                <ChevronUp className="h-5 w-5 text-[var(--text-ter)]" />
              ) : (
                <ChevronDown className="h-5 w-5 text-[var(--text-ter)]" />
              )}
            </button>
            
            {expandedCandidates.has(id) && (
              <div className="border-t border-[var(--mustard)]">
                <ItemValueEntry
                  session={session}
                  itemId={id}
                  onValuesUpdated={onValuesUpdated}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Value Summary Card
// ============================================================================

export function ValueSummaryCard({ 
  session,
}: { 
  session: PrioritizationSession;
}) {
  const stats = useMemo(() => {
    const eligibilities = Object.values(session.itemEligibility);
    return {
      total: session.itemIds.length,
      eligible: eligibilities.filter(e => e.status === 'ELIGIBLE').length,
      withGaps: eligibilities.filter(e => e.status === 'ELIGIBLE_WITH_GAPS').length,
      ineligible: eligibilities.filter(e => e.status === 'INELIGIBLE_STRUCTURAL').length,
    };
  }, [session.itemIds, session.itemEligibility]);
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
      <h4 className="font-medium text-[var(--text-pri)] mb-3">Value Status Summary</h4>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-[var(--green-dim)]">
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{stats.eligible}</div>
          <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Ready</div>
        </div>
        <div className="p-3 bg-[var(--mustard-dim)]">
          <div className="text-2xl font-bold text-[var(--mustard)]">{stats.withGaps}</div>
          <div className="text-xs text-[var(--mustard)]">Need Values</div>
        </div>
        <div className="p-3 bg-[var(--coral-dim)]">
          <div className="text-2xl font-bold text-[var(--coral)]">{stats.ineligible}</div>
          <div className="text-xs text-[var(--coral)]">Ineligible</div>
        </div>
      </div>
      
      {stats.withGaps > 0 && (
        <p className="text-sm text-[var(--mustard)] mt-3 flex items-center gap-1">
          <Clock className="h-4 w-4" />
          Enter values for {stats.withGaps} candidate{stats.withGaps !== 1 ? 's' : ''} to proceed
        </p>
      )}
    </div>
  );
}

export default ItemValueEntry;
